"use client"

import { useMemo, useState } from "react"
import { ArrowUpRight, Info, RefreshCw } from "lucide-react"
import {
  StatBorderGrid,
  ToolPageShell,
  ToolProductCarousel,
  ToolShareBar,
} from "@/components/brand-kit"
import { TOOL_METADATA } from "@/lib/tool-metadata"
import { readCount } from '@/lib/tools/numeric-input'

const FALLBACK_RELATED = [
  "refrigerator-size-calculator",
  "energy-cost-calculator",
  "appliance-energy-calculator",
  "kitchen-space-planner",
]

function buildRelated(slugs: string[], currentSlug: string) {
  const seen = new Set<string>([currentSlug])
  const out: { title: string; category: string; description: string; href: string; time: string }[] = []
  for (const s of [...slugs, ...FALLBACK_RELATED]) {
    if (out.length === 4) break
    if (seen.has(s)) continue
    const m = TOOL_METADATA[s]
    if (!m) continue
    seen.add(s)
    out.push({
      title: m.name,
      category: m.category,
      description: m.shortDescription,
      href: `/tools/${s}`,
      time: "~30 sec",
    })
  }
  return out
}

const META = {
  name: "RV Refrigerator Size Calculator",
  category: "Size & Capacity" as const,
  mediumDescription:
    "Pick an RV fridge sized for your rig type, traveler count, and trip length — and pick the right power architecture (3-way absorption, 2-way compressor, or 12V DC). Sized for boondocking, shore power, or both.",
  faqs: [
    { question: "What's the difference between 2-way and 3-way RV fridges?", answer: "A 2-way (compressor) fridge runs on 12V DC and 120V AC. A 3-way (absorption) fridge adds propane, letting you stay cold off-grid without batteries. Compressor fridges are more efficient on shore power; absorption fridges are better for long boondocking trips." },
    { question: "How big should a fridge for a Class A motorhome be?", answer: "Class A and fifth-wheel rigs typically support 10–16 cu ft residential-style refrigerators. Two travelers on weekend trips need ~12 cu ft; full-timers should plan on 15+ cu ft, often with a separate freezer." },
    { question: "Will a residential fridge work in an RV?", answer: "Yes — many newer Class A and fifth-wheel RVs come with residential 120V AC compressor fridges. Plan for inverter capacity (1000W+) and a battery bank that can support the fridge overnight when boondocking." },
    { question: "How much propane does a 3-way fridge use?", answer: "About 1.5 lbs of propane per day on the propane setting. A 30-lb tank lasts roughly three weeks of fridge-only use, longer if you also run on shore power when available." },
    { question: "Do RV fridges need ventilation?", answer: "Absorption fridges require a roof vent and lower exterior vent for airflow over the cooling unit. Blocked vents are the most common cause of weak cooling. Compressor fridges have similar but smaller ventilation needs." },
    { question: "Can I use a regular dorm fridge in my RV?", answer: "Only on shore power or a strong inverter setup. Standard 120V AC dorm fridges aren't designed for the vibration of travel and don't run on propane or 12V DC. Get a purpose-built RV unit if you'll be off-grid." },
  ],
  relatedTools: ["refrigerator-size-calculator", "energy-cost-calculator", "appliance-energy-calculator"],
}

export function RvRefrigeratorSelectorPageClient() {
  const [rvType, setRvType] = useState("class-a")
  const [travelers, setTravelers] = useState("2")
  const [tripLength, setTripLength] = useState("weekend")
  const [powerSource, setPowerSource] = useState("both")

  // Original calc logic preserved verbatim from the legacy page-client.
  const result = useMemo(() => {
    const numTravelers = readCount(travelers, { min: 1, max: 12 })
    if (numTravelers === null) return null

    let minCuFt = 0
    let maxCuFt = 0

    if (rvType === "class-a" || rvType === "class-c") {
      minCuFt = 8 + (numTravelers * 1.5)
      maxCuFt = 12 + (numTravelers * 2)
    } else if (rvType === "travel-trailer") {
      minCuFt = 6 + (numTravelers * 1.2)
      maxCuFt = 10 + (numTravelers * 1.5)
    } else if (rvType === "fifth-wheel") {
      minCuFt = 10 + (numTravelers * 1.5)
      maxCuFt = 16 + (numTravelers * 2)
    } else { // camper-van
      minCuFt = 3 + (numTravelers * 0.8)
      maxCuFt = 5 + (numTravelers * 1)
    }

    if (tripLength === "extended") {
      minCuFt *= 1.3
      maxCuFt *= 1.3
    } else if (tripLength === "full-time") {
      minCuFt *= 1.5
      maxCuFt *= 1.5
    }

    let fridgeType = ""
    let powerOptions: string[] = []
    if (powerSource === "propane" || powerSource === "both") {
      fridgeType = "Absorption (3-way) refrigerator"
      powerOptions = ["12V DC (while driving)", "120V AC (shore power)", "Propane (off-grid)"]
    } else if (powerSource === "electric") {
      fridgeType = "Compressor (2-way) refrigerator"
      powerOptions = ["12V DC (battery / solar)", "120V AC (shore power)"]
    } else {
      fridgeType = "12V DC compressor refrigerator"
      powerOptions = ["12V DC only"]
    }

    const avgCuFt = Math.round((minCuFt + maxCuFt) / 2 * 10) / 10
    const recommendedSize = `${minCuFt.toFixed(1)} – ${maxCuFt.toFixed(1)} cu ft`

    return { recommendedSize, avgCuFt, fridgeType, powerOptions }
  }, [rvType, travelers, tripLength, powerSource])

  return (
    <ToolPageShell
      toolName={META.name}
      heroEyebrow={META.category}
      heroTitlePre="An RV fridge that"
      heroTitleItalic="works"
      heroTitlePost="off-grid."
      heroSubtitle={META.mediumDescription}
      heroMeta={["⏱ ~30 sec", "Sizing for class A/C, trailer, fifth-wheel, van"]}
      heroShare={<ToolShareBar title={META.name} />}
      heroRight={
        <StatBorderGrid
          stats={[
            { value: "3", label: "Power modes", note: "12V / AC / LP" },
            { value: "1.5", label: "Lb LP / day", note: "Absorption" },
            { value: "10–16", label: "Cu ft Class A", note: "Typical" },
            { value: "3–5", label: "Cu ft camper van", note: "Typical" },
          ]}
          className="w-full max-w-md md:max-w-none md:w-auto mx-auto md:mx-0"
        />
      }
      faqs={META.faqs}
      relatedTools={buildRelated(META.relatedTools, "rv-refrigerator-size-calculator")}
      cta={{
        title: "Find a counter-depth or compact fridge for your build.",
        body: "Browse counter-depth and top-freezer refrigerators — useful starting points if you're spec'ing a residential-style fridge for an RV conversion.",
        buttonLabel: "Browse counter-depth fridges",
        buttonHref: "/counter-depth-refrigerators",
      }}
    >
      {/* ============ CALCULATOR ============ */}
      <section className="bg-gray-50 py-8 md:py-12">
        <div className="max-w-[1120px] mx-auto px-6 md:px-8 space-y-6">
          <div className="grid lg:grid-cols-2 gap-6 lg:items-stretch">
            <div className="bg-white rounded-xl p-6 md:p-7 border border-gray-900 flex flex-col">
              <div className="flex items-center gap-3 mb-2">
                <span className="font-mono text-[12px] font-bold tracking-[0.15em] text-[#ff9951]">
                  STEP 01
                </span>
                <div className="flex-1 h-[1px] bg-gray-300" />
              </div>
              <h2
                className="text-gray-900 font-black"
                style={{ fontSize: 28, letterSpacing: "-0.025em", lineHeight: 1.05 }}
              >
                Rig type, travelers,
                <br />
                trip length, power.
              </h2>
              <p className="text-[14px] text-gray-600 mt-2 leading-[1.5]">
                Sized from typical RV fridge spec ranges by class. Power source determines compressor vs absorption.
              </p>

              <div className="mt-7 space-y-5">
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-[0.12em] text-gray-900 mb-2">
                    RV type
                  </label>
                  <select
                    value={rvType}
                    onChange={(e) => setRvType(e.target.value)}
                    className="tool-input-clean w-full px-4 py-4 text-[18px] font-bold rounded-lg outline-none border border-gray-300 focus:border-[#ff9951] bg-white"
                  >
                    <option value="class-a">Class A motorhome</option>
                    <option value="class-c">Class C motorhome</option>
                    <option value="travel-trailer">Travel trailer</option>
                    <option value="fifth-wheel">Fifth wheel</option>
                    <option value="camper-van">Camper van</option>
                  </select>
                </div>
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-[0.12em] text-gray-900 mb-2">
                    Travelers
                  </label>
                  <input
                    type="number"
                    min={1}
                    max={12}
                    step="1"
                    value={travelers}
                    onChange={(e) => setTravelers(e.target.value)}
                    className="tool-input-clean w-full px-4 py-4 text-[18px] font-bold rounded-lg outline-none border border-gray-300 focus:border-[#ff9951] bg-white"
                  />
                </div>
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-[0.12em] text-gray-900 mb-2">
                    Trip length
                  </label>
                  <select
                    value={tripLength}
                    onChange={(e) => setTripLength(e.target.value)}
                    className="tool-input-clean w-full px-4 py-4 text-[18px] font-bold rounded-lg outline-none border border-gray-300 focus:border-[#ff9951] bg-white"
                  >
                    <option value="weekend">Weekend</option>
                    <option value="extended">Extended (1–3 weeks)</option>
                    <option value="full-time">Full-time</option>
                  </select>
                </div>
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-[0.12em] text-gray-900 mb-2">
                    Power source
                  </label>
                  <select
                    value={powerSource}
                    onChange={(e) => setPowerSource(e.target.value)}
                    className="tool-input-clean w-full px-4 py-4 text-[18px] font-bold rounded-lg outline-none border border-gray-300 focus:border-[#ff9951] bg-white"
                  >
                    <option value="both">Both (propane + electric)</option>
                    <option value="propane">Propane primary</option>
                    <option value="electric">Electric (compressor)</option>
                    <option value="12v">12V DC only</option>
                  </select>
                  <div className="text-[11px] text-gray-500 mt-1.5 flex items-center gap-1.5">
                    <Info className="w-3.5 h-3.5" /> Determines compressor vs absorption architecture.
                  </div>
                </div>
                <div className="pt-2 text-[11px] uppercase tracking-[0.12em] text-gray-500 font-bold flex items-center gap-1.5">
                  <RefreshCw className="w-3 h-3" /> Updates live as you type
                </div>
              </div>
            </div>

            {/* Result panel */}
            <div className="flex flex-col">
              <div
                className="rounded-2xl p-6 md:p-8 relative overflow-hidden text-white flex-1"
                style={{ background: "linear-gradient(135deg, #0469f0 0%, #0053c4 100%)" }}
              >
                <div
                  aria-hidden
                  className="absolute top-0 right-0 w-[340px] h-[340px] rounded-full pointer-events-none"
                  style={{
                    background: "rgba(255,153,81,0.35)",
                    filter: "blur(60px)",
                    transform: "translate(25%, -35%)",
                  }}
                />
                <div className="relative">
                  <div className="flex items-center justify-between mb-8 flex-wrap gap-2">
                    <div className="text-[11px] font-bold uppercase tracking-[0.15em] text-white/70">
                      Recommended size
                    </div>
                    <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.15em] text-white/60">
                      <RefreshCw className="w-3 h-3" /> Updated live
                    </div>
                  </div>
                  <div
                    className="font-black"
                    style={{
                      fontSize: "clamp(40px, 6vw, 72px)",
                      letterSpacing: "-0.045em",
                      lineHeight: 0.95,
                    }}
                  >
                    {result?.avgCuFt ?? 0}
                    <span className="text-[20px] md:text-[24px] font-black tracking-tighter text-white/60 ml-2">
                      cu ft avg
                    </span>
                  </div>
                  <div className="mt-3 text-[14px] text-white/70 leading-[1.5]">
                    Range: {result?.recommendedSize ?? "—"}
                  </div>
                  <div className="mt-4 text-[14px] text-white/80 leading-[1.5]">
                    {result?.fridgeType ?? "Enter your rig and power setup."}
                  </div>
                  {result?.powerOptions && (
                    <div className="mt-6 space-y-1.5">
                      <div className="text-[10px] uppercase tracking-[0.15em] text-white/60 font-bold">
                        Power options
                      </div>
                      {result.powerOptions.map((opt) => (
                        <div key={opt} className="text-[13px] text-white/85 leading-[1.4]">
                          • {opt}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ FIRST CAROUSEL ============ */}
      <section className="bg-white py-4 md:py-6">
        <ToolProductCarousel
          eyebrow="Step 02 · Residential references"
          title="Counter-depth fridges for RV builds"
          description="If you're spec'ing a residential-style 120V AC compressor fridge for a Class A or fifth-wheel."
          collectionType="counter-depth"
          fallbackCollectionType="french-door"
        />
      </section>

      {/* ============ METHODOLOGY ============ */}
      <section className="bg-gray-50 py-8 md:py-12 border-y border-gray-200">
        <div className="max-w-[1120px] mx-auto px-6 md:px-8">
          <div className="grid md:grid-cols-[1fr_1.3fr] gap-8 md:gap-12 items-start">
            <div>
              <div className="text-[11px] font-bold uppercase tracking-[0.15em] mb-3 text-[#ff9951]">
                Under the hood
              </div>
              <h2
                className="text-gray-900 font-black"
                style={{ fontSize: "clamp(24px, 3vw, 36px)", letterSpacing: "-0.03em", lineHeight: 1.05 }}
              >
                Sized from <em className="italic text-[#ff9951]">RV class</em>, then trip-adjusted.
              </h2>
              <p className="text-[14px] text-gray-600 mt-4 max-w-[400px] leading-[1.55]">
                Base ranges reflect typical RV fridge spec sheets by class. Trip-length multipliers reflect bulk-storage needs for extended and full-time travel.
              </p>
            </div>
            <ol className="list-none p-0 m-0 border-t border-gray-900">
              {[
                {
                  n: "01",
                  t: "Class baseline",
                  d: "Class A/C: 8 + 1.5/traveler. Travel trailer: 6 + 1.2/traveler. Fifth-wheel: 10 + 1.5/traveler. Camper van: 3 + 0.8/traveler.",
                },
                {
                  n: "02",
                  t: "Trip-length multiplier",
                  d: "Weekend ×1.0. Extended (1–3 weeks) ×1.3. Full-time ×1.5.",
                },
                {
                  n: "03",
                  t: "Power architecture",
                  d: "Propane / both → 3-way absorption. Electric → 2-way compressor. 12V DC only → DC-native compressor unit (typical for camper vans on solar).",
                },
              ].map((s) => (
                <li
                  key={s.n}
                  className="grid grid-cols-[auto_1fr_auto] gap-4 sm:gap-7 items-center py-5 sm:py-6 border-b border-gray-900"
                >
                  <div className="font-mono text-sm font-bold tracking-[0.05em] min-w-[28px] sm:min-w-[34px] text-[#ff9951]">
                    {s.n}
                  </div>
                  <div>
                    <h3
                      className="text-gray-900"
                      style={{ fontSize: 22, fontWeight: 800, letterSpacing: "-0.02em" }}
                    >
                      {s.t}
                    </h3>
                    <p className="mt-1.5 text-[14px] text-gray-600 leading-[1.55]">{s.d}</p>
                  </div>
                  <ArrowUpRight className="w-4 h-4 sm:w-5 sm:h-5 text-gray-500 flex-shrink-0" />
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      {/* ============ SECOND CAROUSEL ============ */}
      <section className="bg-white py-4 md:py-6">
        <ToolProductCarousel
          eyebrow="You might also like"
          title="Top-freezer fridges for compact builds"
          description="Simple architecture, lower power draw — a common reference for travel trailers and smaller rigs."
          collectionType="top-freezer"
          fallbackCollectionType="counter-depth"
        />
      </section>
    </ToolPageShell>
  )
}
